import type { TeamBudgetDivision } from '../types'

/** A positive amount in currency units, in integer cents; null for anything else. */
const positiveCents = (
  value: number | string | null | undefined
): number | null => {
  const dollars = Number(value)

  return Number.isFinite(dollars) && dollars > 0
    ? Math.round(dollars * 100)
    : null
}

/**
 * Sep 24 — the deposit a Team Budget division asks for — its Minimum To Qualify
 * (`cost_distribution_threshold`), pre-tax — in integer cents; null when the
 * division is not a Team Budget one or sets no positive deposit.
 */
export const teamBudgetDepositCents = (
  division?: TeamBudgetDivision | null
): number | null =>
  division?.cost_distribution_enabled
    ? positiveCents(division.cost_distribution_threshold)
    : null

/**
 * Sep 24 — the whole team fee (`team_cost`), pre-tax, in integer cents: the most a
 * rep may pay at registration ("up to full of whatever the team is owing"). Null
 * when the division is not a Team Budget one or has no positive fee.
 */
export const teamBudgetFeeCents = (
  division?: TeamBudgetDivision | null
): number | null =>
  division?.cost_distribution_enabled ? positiveCents(division.team_cost) : null

/**
 * Sep 24 (R6) — a Team Budget TEAM rep always goes to the payment step, the one
 * place they can choose to pay toward the deposit. Callers check it BEFORE the
 * legacy coupon and "free registration" shortcuts: a Team Budget session prices at
 * zero whatever the coupon, so those shortcuts would confirm the registration
 * without ever offering the choice. Players and every other division route as
 * before.
 */
export const goesToTeamBudgetPaymentStep = ({
  isTeamRep,
  division,
}: {
  isTeamRep?: boolean | null
  division?: TeamBudgetDivision | null
}): boolean => Boolean(isTeamRep && division?.cost_distribution_enabled)

/**
 * Whether the payment step offers to pay now: a Team Budget rep, a deposit (paying
 * it is the default) and a team fee, and a competition that takes online payments.
 */
export const offersTeamBudgetPayment = ({
  isTeamRep,
  division,
  hasPaymentProvider,
}: {
  isTeamRep?: boolean | null
  division?: TeamBudgetDivision | null
  hasPaymentProvider: boolean
}): boolean =>
  goesToTeamBudgetPaymentStep({ isTeamRep, division }) &&
  hasPaymentProvider &&
  teamBudgetDepositCents(division) !== null &&
  teamBudgetFeeCents(division) !== null
